import { useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import _isEmpty from 'lodash/isEmpty'
import { useIntl } from 'react-intl'

import { useAppSelector } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'

import { DateCell, MobileSortBy, Table } from 'components/Table'
import type { LicenseType } from 'features/licenses/types'
import { getSortOptions, sortByDate } from 'components/Table/utils'
import LicensingDescription from './LicensingDescription'
import AddedLicenseNote from './AddedLicenseNote'
import { homeAddedLicensesSelector } from 'features/home/selectors'
import {
  AddedLicensesContainer,
  AddedLicensesHeader,
  RowsCount,
  Title,
  StyledTableInnerWrapper,
  StyledTableOuterWrapper,
  CellStyles,
  CellStylesDate,
} from './styles'
import AddedLicensesMobile from './AddedLicensesMobile'
import { HomeNoLicenses } from '../styles'
import { homeLicensingMessages } from '../messages'

const DEFAULT_SORT = { id: 'name', desc: false }

function AddedLicenses() {
  const { formatMessage } = useIntl()

  const licenses = useAppSelector<LicenseType[]>(homeAddedLicensesSelector)
  const licensesCount = Array.isArray(licenses) ? licenses.length : 0

  const headers = useMemo(
    () => ({
      name: {
        label: formatMessage(homeLicensingMessages.name),
        id: 'name',
      },
      code: {
        label: formatMessage(homeLicensingMessages.licenseCode),
        id: 'code',
      },
      orderNumber: {
        label: formatMessage(homeLicensingMessages.orderNumber),
        id: 'orderNumber',
      },
      customerPO: {
        label: formatMessage(homeLicensingMessages.purchaseOrder),
        id: 'customerPO',
      },
      expiresAt: {
        label: formatMessage(homeLicensingMessages.ends),
        id: 'expiresAt',
      },
      activated: {
        label: formatMessage(homeLicensingMessages.activated),
        id: 'activated',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<LicenseType>[] = useMemo(
    () => [
      {
        Header: headers.name.label,
        accessor: 'name',
        Cell: LicensingDescription,
        percentWidth: '25%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.code.label,
        accessor: 'code',
        percentWidth: '10%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.orderNumber.label,
        accessor: 'orderNumber',
        percentWidth: '13%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.customerPO.label,
        accessor: 'customerPO',
        percentWidth: '14%',
        cellStyles: CellStyles,
      },
      {
        Header: headers.activated.label,
        accessor: 'activated',
        percentWidth: '9%',
        Cell: DateCell,
        sortType: sortByDate,
        cellStyles: CellStylesDate,
      },
      {
        Header: headers.expiresAt.label,
        accessor: 'expiresAt',
        percentWidth: '9%',
        Cell: DateCell,
        sortType: sortByDate,
        cellStyles: CellStylesDate,
      },
      {
        Header: formatMessage(homeLicensingMessages.notes),
        accessor: 'notes',
        percentWidth: '18%',
        Cell: (cell: Cell<LicenseType>) => (
          <AddedLicenseNote
            original={cell.row.original}
            initialNote={cell.value}
          />
        ),
        cellStyles: CellStyles,
      },
    ],
    [formatMessage, headers]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  const [sortBy, setSortBy] = useState<SortingRule<LicenseType>>(DEFAULT_SORT)

  return (
    <AddedLicensesContainer>
      <AddedLicensesHeader>
        <Title>{formatMessage(homeLicensingMessages.appliedLicenses)}</Title>
        <RowsCount>
          {licensesCount} {formatMessage(commonMessages.items)}
        </RowsCount>
      </AddedLicensesHeader>

      {_isEmpty(licenses) ? (
        <HomeNoLicenses>
          {formatMessage(homeLicensingMessages.noLicensesAppplied)}
        </HomeNoLicenses>
      ) : (
        <StyledTableOuterWrapper>
          <StyledTableInnerWrapper>
            <MobileSortBy
              headers={headers}
              sortOptions={sortOptions}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
            <Table
              columns={columns}
              data={licenses}
              defaultSortBy={DEFAULT_SORT}
              mobileViewComponent={AddedLicensesMobile}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          </StyledTableInnerWrapper>
        </StyledTableOuterWrapper>
      )}
    </AddedLicensesContainer>
  )
}

export default AddedLicenses
